<template>
    <div>
        <heroLayout
            id="warranty-hero"
            :enable-login-offset="true"
            display-separator
            :overlay-style-overrides="overlayStyleOverrides"
            :enable-new-hero="true"
            :new-hero-class="heroClass"
        >
            <template #title>
                <span class="title">{{ title }}</span>
            </template>
            <template #overlay-body>
                <h2 class="subtitle">{{ section1.subtitle }}</h2>
                <p class="description">{{ section1.description1 }}</p>
                <p class="description padding-bottom-10">{{ section1.description2 }}</p>
                <h2 class="subtitle">Benefits:</h2>
                <ul class="padding-bottom-10">
                    <div v-for="(benefit, index) in benefits" :key="index">
                        <li>
                            <p class="no-bottom-padding description">{{ benefit }}</p>
                        </li>
                    </div>
                </ul>
                <h2 class="subtitle">{{ section2.subtitle }}</h2>
                <p class="description padding-bottom-10">{{ section2.description }}</p>
                <div class="padding-bottom-10 button-container">
                    <primary-link
                        href="https://play.google.com/store/apps/details?id=com.carsaver.connect"
                        target="_blank"
                        aria-label="Google App Store (New Window)"
                    >
                        <b-img :src="googlePlayIcon" alt="Google Play Store download link for CarSaver Protect App" />
                    </primary-link>
                    <primary-link
                        href="https://itunes.apple.com/us/app/carsaver-protect/id1229852030"
                        target="_blank"
                        class="ml-3"
                        aria-label="iTunes App Store (New Window)"
                    >
                        <b-img :src="appStoreIcon" alt="Apple App Store download link for CarSaver Protect App" />
                    </primary-link>
                    <p></p>
                </div>
                <b-container class="text-center">
                    <primary-btn class="mb-3 mt-3 w-100 max-width--250" @click.native="gotoFaqs">
                        {{ buttonText }}
                    </primary-btn>
                    <b-container fluid class="d-flex justify-content-center no-bottom-padding px-0">
                        <p class="w-100 subtext align-content-center mx-n1">
                            {{ subtexts.subtext1 }}
                            <a href="tel:844-355-3154">{{ phoneNumberFormatted }}</a>
                            {{ subtexts.subtext2 }}
                        </p>
                    </b-container>
                </b-container>
            </template>
        </heroLayout>
        <b-container fluid class="d-flex justify-content-center no-bottom-padding">
            <p class="disclaimer no-bottom-padding px-2">
                {{ disclaimers.disclaimer1 }}
                <a href="/warranty/faq" target="_blank" rel="noopener">
                    {{ disclaimers.disclaimerUrl }}
                </a>
                {{ disclaimers.disclaimer2 }}
                <a href="tel:800-217-4800">{{ disclaimers.disclaimerContact }}</a>
                {{ disclaimers.disclaimer3 }}
            </p>
        </b-container>
        <floating-footer>
            <primary-btn @click.native="gotoFaqs">{{ buttonText }}</primary-btn>
        </floating-footer>
    </div>
</template>

<script>
import GooglePlayIcon from "../images/google-play.png";
import AppStoreIcon from "../images/appstore.png";
import heroLayout from "Components/HeroLayout";
import FloatingFooter from "Components/FloatingFooter";
import primaryBtn from "Components/Buttons/PrimaryBtn";
import PrimaryLink from "Components/Links/primaryLink";
import { get } from "vuex-pathify";

export default {
    name: "Warranty",
    components: {
        heroLayout,
        FloatingFooter,
        primaryBtn,
        PrimaryLink,
    },
    data() {
        return {
            googlePlayIcon: GooglePlayIcon,
            appStoreIcon: AppStoreIcon,
            overlayStyleOverrides: {
                maxWidth: "800px",
            },
            heroClass: "warranty-hero",
            benefits: [
                "Lifetime Warranty* standard - at no cost to you",
                "Coverage on engine, transmission, and drive train",
                "Unlimited miles, unlimited time – for as long as you own the vehicle",
            ],
            title: "CarSaver’s Lifetime Warranty*",
            section1: {
                subtitle: "We’ve got you covered for life!",
                description1: `As vehicle technology has become more complex, repair costs have increased.
                A new engine can cost more than $3,000, and a new transmission can cost over $2,500 - not
                to mention how much it costs to fix or replace seals and gaskets, engine mounts, exhaust
                manifolds, wheel bearings, drive shafts, and many other components.`,
                description2: `CarSaver's Lifetime Warranty* covers these repairs and more on your engine,
                transmission, and drivetrain for unlimited miles and unlimited time with no deductible.`,
            },
            section2: {
                subtitle: "Download the app:",
                description: `With CarSaver’s Lifetime Warrranty you can download the app to store your
                contract and manage any claims. `,
            },
            buttonText: "Lifetime Warranty FAQ’s",
            subtexts: {
                subtext1: "For questions, you can also call a CarSaver " + this.$t("deal.shopperText") + " at",
                subtext2: " or for a list of frequently asked questions",
            },
            contact: "(844) 355-3154",
            disclaimers: {
                disclaimer1: `*The CarSaver Lifetime Warranty is only available on eligible vehicles. CarSaver’s
                Lifetime Warranty covers all repairs to your engine, transmission, and drivetrain for your vehicle
                for as long as you own it. Parts of like kind and quality may
                be used for repairs, including remanufactured parts. You will be responsible for any charges
                not covered under your contract, including maintenance work or non-covered repairs. CarSaver’s
                Lifetime Warranty only covers claims that would not be covered by the manufacturer’s warranty.
                CarSaver’s Lifetime Warranty may only be obtained through a CarSaver Certified Dealer and in
                compliance with CarSaver policies. See the CarSaver Limited Warranty Agreement for complete
                details, which is available from your CarSaver dealer prior to obtaining the warranty.
                Effective March 1, 2021, before coverage for maintenance begins, there is a waiting
                period of 4 months and 4,000 miles (30 days and 1,000 miles in Georgia) after the effective
                 date of the service contract. See CarSaver.com for vehicles eligible for a lifetime warranty
                 or contact your CarSaver dealer to learn about your particular vehicle’s eligibility. You can
                 also view frequently asked questions regarding this product at `,
                disclaimer2: ` or contact our customer support at `,
                disclaimer3: ` with any specific questions.`,
                disclaimerUrl: "https://www.carsaver.com/warranty/faq",
                disclaimerContact: "(800) 217-4800",
            },
        };
    },
    computed: {
        phoneNumberFormatted: get("campaign/phoneNumberFormatted"),
    },
    methods: {
        gotoFaqs() {
            this.$router.push({ name: "warranty-faq" });
        },
    },
};
</script>

<style lang="scss" scoped>
.title {
    @include media-breakpoint-up("md") {
        padding-left: px2rem(20);
        padding-right: px2rem(20);
    }
}
.subtitle {
    font-size: px2rem(18);
    line-height: 1.3;
    font-weight: bold;
    margin-bottom: px2rem(10);
}
.description {
    font-size: px2rem(16);
    letter-spacing: 0;
    line-height: 1.3;
    margin-bottom: px2rem(10);
}
.no-padding {
    padding-left: 0;
    padding-right: 0;
}
.no-bottom-padding {
    padding-bottom: 0;
    margin-bottom: 0;
}
.subtext {
    font-size: px2rem(14);
    max-width: 520px;
}
.disclaimer {
    font-size: px2rem(12);
    line-height: 1.3;
    max-width: 800px;
    font-style: italic;
    margin-top: px2rem(20);
}
.padding-bottom-10 {
    padding-bottom: px2rem(10);
}
.max-width--250 {
    max-width: 250px;
}
.button-container {
    text-align: center;

    @include media-breakpoint-up("md") {
        text-align: left;
    }
}
</style>
